// Plan madde 10.1 — operator session bootstrap hook.
//
// Wraps GET /ops/v1/me as a TanStack Query with sensible defaults for an
// operator session: cache for the lifetime of the page (refetchOnMount=
// "stale" via 5-min staleTime), refetch on window focus (operator
// switches tabs after a long idle → re-validate scope tier + roles in
// case admin tweaked them mid-session), and the shared QueryClient retry
// policy. That policy honors Spec 58 envelope retryability, preserves bounded
// network fallback, and applies `retry_after_ms` without retrying early.
//
// Returns the canonical TanStack Query result so callers (usePermission,
// RequireRole, route-level data hooks) can read .data, .isLoading,
// .error individually. The shape of .data comes from openapi.yaml's
// /ops/v1/me response → types.gen.ts → openapi-fetch typed client; no
// hand-typed interface here.
//
// Error path: TanStack Query's onError fires showErrorToast(error, t)
// from the caller side (this hook itself doesn't show the toast — the
// caller decides whether to surface it; the home page might want a full
// "session expired" page rather than a toast on the same view).
// The API client recovers a rejected session before a safe GET settles;
// if renewal fails, the root auth bridge starts sign-in with the return path.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { unwrap } from "@/api/envelope";
import { shouldRetryQuery } from "@/api/queryClient";
import { operatorGraph } from "@/api/operator-graph";
import type { paths } from "@/api/types.gen";

/**
 * Resolved scope returned by GET /ops/v1/me. Tip openapi.yaml'dan
 * türetilen `data` slot'u — bu hook'un cache'ledigi şey TAM olarak
 * backend'in döndürdüğü payload (envelope wrapper soyutlanır by unwrap).
 */
export type OperatorMe = NonNullable<
  paths["/ops/v1/me"]["get"]["responses"][200]["content"]["application/json"]["data"]
>;

export const OPERATOR_ME_QUERY_KEY = ["operator", "me"] as const;

/**
 * 5 minutes — long enough that nav/permission decisions across the
 * session don't refetch on every render, short enough that a role
 * change from the IAM screen propagates within ~5 min without a
 * full reload. Manual invalidation via queryClient.invalidateQueries(
 * { queryKey: OPERATOR_ME_QUERY_KEY }) lands when plan madde 11's
 * IAM mutation flow (admin grants a role) needs immediate effect.
 */
const STALE_TIME_MS = 5 * 60_000;

/**
 * Compatibility export for existing session tests and consumers. This is the
 * exact console-wide predicate, not an operator-session-specific copy; the
 * paired retry delay is inherited from QueryClient defaults.
 */
export const shouldRetryOperatorMe = shouldRetryQuery;

export function useOperatorMe(): UseQueryResult<OperatorMe> {
  return useQuery<OperatorMe>({
    queryKey: OPERATOR_ME_QUERY_KEY,
    queryFn: () => unwrap(operatorGraph.GET("/ops/v1/me")),
    staleTime: STALE_TIME_MS,
    // Operator console is short-session by spec 60 (30-min idle); a
    // background refetch on focus catches admin role tweaks without a
    // full reload, and the cost is one /ops/v1/me round-trip.
    refetchOnWindowFocus: true,
    retry: shouldRetryQuery,
  });
}
